<template>
  <ui-dialog
    :show="isOpen"
    persistent
    scrollable
    max-width="623"
    @input="close"
  >
    <template #header>
      <h3 class="more-hours-categories-dialog__heading">
        {{ $t("message.facilityDetails.moreHours.title") }}
      </h3>
      <p class="more-hours-categories-dialog__description">
        {{
          $t(
            `message.facilityDetails.moreHours.categoriesDialog.${
              facilitiesIds.length > 1 ? "descriptionMassEdit" : "description"
            }`
          )
        }}
      </p>
    </template>
    <template #default>
      <div class="available-categories">
        <p class="available-categories__title">
          {{
            $t(
              `message.facilityDetails.moreHours.categoriesDialog.${
                deleteMode ? "chooseCategoriesToDelete" : "chooseCategory"
              }`
            )
          }}
        </p>

        <template v-if="loaded && deleteMode && fetchedCategories.length > 0">
          <v-list class="more-hours-categories-dialog__delete-list">
            <v-virtual-scroll
              :items="fetchedCategories"
              :item-height="listItemHeight"
              :height="deleteListHeight"
            >
              <template v-slot:default="{ item }">
                <v-list-item
                  :key="item.id"
                  :ripple="false"
                  @click="toggleDeleteCategory(item)"
                  class="more-hours-categories-dialog__delete-list-item"
                  :class="{ selected: item.isSelectedToDelete }"
                >
                  <v-list-item-content class="d-flex align-center">
                    <v-icon
                      size="21"
                      :color="item.isSelectedToDelete ? 'pink' : 'text'"
                      class="mr-3"
                    >
                      {{ item.isSelectedToDelete ? "icon-trash" : "icon-plus" }}
                    </v-icon>
                    <p>
                      {{ item.display_name }}
                    </p>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-virtual-scroll>
          </v-list>
        </template>
        <template
          v-else-if="loaded && !deleteMode && availableCategories.length > 0"
        >
          <ui-button
            v-for="category in availableCategories"
            :key="category.id"
            class="available-categories__choose-btn"
            :class="{ 'd-block': deleteMode }"
            icon="mdi-plus"
            @click="chooseCategory(category)"
            outlined
          >
            {{ category.name }}
          </ui-button>
        </template>
        <template v-else-if="loaded">
          <p class="available-categories__no-categories">
            {{
              $t(
                "message.facilityDetails.moreHours.categoriesDialog.noCategories"
              )
            }}
          </p>
        </template>
        <template v-else>
          <div class="loading-in-progress">
            <SectionLoader :status="loading" :opacity="1" />
          </div>
        </template>
      </div>
      <div class="set-categories" v-if="!deleteMode && tempValues.length > 0">
        <MoreHoursCategoryListItem
          class="set-categories__list-item"
          v-for="(item, index) in tempValues"
          :key="index"
          :item="item"
          :editable="true"
          @delete="handleDelete"
          @edit="handleEdit"
        />
      </div>
    </template>
    <template #actions>
      <div class="more-hours-categories-dialog__actions">
        <ui-button outlined @click="close">
          {{ $t("message.common.cancel") }}
        </ui-button>
        <ui-button color="primary" @click="save">
          {{ $t("message.common.save") }}
        </ui-button>
      </div>
    </template>
  </ui-dialog>
</template>

<script>
import cuid from "cuid";
import serviceHandler from "@/helpers/handlers/service";
import facilitiesService from "@/services/facilities";

import SectionLoader from "@/components/Loader/SectionLoader.vue";
import MoreHoursCategoryListItem from "@/components/FacilityDetails/GmbMoreHours/MoreHoursCategoryListItem.vue";

export default {
  name: "MoreHoursCategoriesDialog",
  data() {
    return {
      fetchedCategories: [],
      listItemHeight: 50,
      loaded: false,
      loading: false
    };
  },
  components: { SectionLoader, MoreHoursCategoryListItem },
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    facilitiesIds: {
      type: Array,
      default: () => []
    },
    tempValues: {
      type: Array,
      default: () => []
    },
    deleteMode: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    tempValuesTypes() {
      return this.tempValues.map(category => category.type);
    },
    availableCategories() {
      return this.fetchedCategories
        .filter(
          category => !this.tempValuesTypes.includes(category.hours_type_id)
        )
        .map(category => {
          return {
            name: category.display_name,
            type: category.hours_type_id,
            id: category.hours_type_id
          };
        });
    },
    deleteListHeight() {
      return this.fetchedCategories && this.fetchedCategories.length < 8
        ? this.listItemHeight * this.fetchedCategories.length
        : this.listItemHeight * 8;
    }
  },
  watch: {
    async isOpen(newVal) {
      if (newVal) {
        await this.fetchCategories();

        if (this.deleteMode) {
          this.fetchedCategories = [
            ...this.fetchedCategories.map(category => {
              return {
                ...category,
                isSelectedToDelete: this.tempValues.some(
                  value => value.type === category.hours_type_id
                ),
                id: cuid()
              };
            })
          ];
        }
      } else {
        this.loaded = false;
        this.fetchedCategories = [];
      }
    }
  },
  methods: {
    async fetchCategories() {
      this.loading = true;

      this.fetchedCategories = await serviceHandler(
        facilitiesService.fetchFacilitiesMoreHours({
          facilitiesIds: this.facilitiesIds,
          onlySet: this.deleteMode
        })
      );

      this.loaded = true;
      this.loading = false;
    },
    chooseCategory(category) {
      this.handleEdit(category);
    },

    setTempValues(categories) {
      this.$emit("setTempValues", categories);
    },

    handleDelete(id) {
      this.setTempValues([...this.tempValues.filter(value => value.id !== id)]);
    },

    toggleDeleteCategory(category) {
      const foundIndex = this.fetchedCategories.findIndex(
        ({ id }) => id === category.id
      );

      if (foundIndex > -1) {
        this.fetchedCategories[foundIndex].isSelectedToDelete = !this
          .fetchedCategories[foundIndex].isSelectedToDelete;
      }
    },

    handleEdit(category) {
      this.$emit("edit", category);
    },

    close() {
      this.$emit("close");
    },

    save() {
      if (this.deleteMode) {
        this.$emit("saveDelete", [
          ...this.fetchedCategories
            .filter(cat => cat.isSelectedToDelete)
            .map(cat => {
              return {
                name: cat.display_name,
                type: cat.hours_type_id
              };
            })
        ]);
      } else {
        this.$emit("save", [...this.tempValues]);
      }
      this.close();
    }
  }
};
</script>

<style lang="scss" scoped>
.more-hours-categories-dialog {
  &__heading {
    line-height: 36px;
  }

  &__description {
    @include font-size(14px);
    color: $text;
    opacity: 0.4;
    line-height: 20px;
    margin-bottom: 0;
    padding: $spacing 0 $spacing * 1.5;
    border-bottom: 1px solid $light-3;
  }

  &__actions {
    margin-top: $spacing * 6;
    width: 100%;
    display: flex;
    justify-content: center;

    & > button:first-child {
      margin-right: $spacing;
    }

    & > button:last-child {
      margin-left: $spacing;
      min-width: 230px !important;
    }
  }

  &__delete-list {
    .v-list-item {
      &__content {
        * {
          flex: unset;
        }
      }
    }
  }

  &__delete-list-item {
    height: 46px;
    border: 1px solid $text;
    border-radius: 30px;
    overflow: hidden;
    @include font-size(14px);
    border: 1px solid rgba($text, 0.14);
    transition: all 0.15s ease-in;
    margin-right: $spacing;

    p {
      margin-bottom: 0;
    }

    &.selected {
      border-color: $pink;
      background-color: $pink-light;
    }
  }
}

.available-categories {
  &__title {
    @include font-size(16px);
    margin-bottom: $spacing * 2.5;
    font-weight: 500;
    color: $text;
  }

  &__choose-btn {
    margin: $spacing * 0.5 0;

    &:not(:last-child) {
      margin-right: $spacing;
    }
  }

  &__no-categories {
    @include font-size(14px);
    color: $text;
    line-height: 20px;
    margin-bottom: 0;
  }
}

.set-categories {
  display: flex;
  flex-direction: column;

  &__list-item {
    border-top: 1px solid $light-3;
    padding-top: $spacing * 2;
    margin-top: $spacing * 2;
  }
}

.loading-in-progress {
  min-height: 100px;
  position: relative;
}
</style>
